import React from "react";
import { LegalPageLayout, P, Ul, Strong } from "../components/site/LegalPageLayout";
import Seo from "../components/site/Seo";

const SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    body: (
      <>
        <P>
          These Terms &amp; Conditions govern every photography engagement between you
          (&quot;the Client&quot;) and Restaurant Photographer (&quot;the Studio&quot;,
          &quot;we&quot;, &quot;us&quot;) operating at <a href="https://restaurantphotographer.com.au" className="text-ember underline-offset-4 hover:underline">restaurantphotographer.com.au</a>.
        </P>
        <P>
          By confirming a quote in writing (email, SMS or signed agreement) or by paying
          a deposit, you accept these terms. We&apos;ve kept them in plain English. If
          anything is unclear, call us on <a href="tel:0494756111" className="text-ember underline-offset-4 hover:underline">0494 756 111</a> before
          paying.
        </P>
      </>
    ),
  },
  {
    id: "quotes",
    title: "Quotes & bookings",
    body: (
      <>
        <P>
          Quotes are issued in writing and are valid for <Strong>30 days</Strong> from
          the date of issue unless otherwise stated. Quotes are based on the brief you
          share with us — menu size, channels, suburb, dish complexity and intended
          shoot date.
        </P>
        <P>
          A booking is confirmed only when both of the following are in place:
        </P>
        <Ul items={[
          "You have accepted the quote in writing (email reply is sufficient).",
          "A 25% deposit has been received (see Deposits & Payments below).",
        ]} />
        <P>
          Until both are in place, the date is held informally and may be released to
          another client.
        </P>
      </>
    ),
  },
  {
    id: "payments",
    title: "Deposits & payments",
    body: (
      <>
        <P>
          We require a <Strong>25% deposit</Strong> to secure the shoot date. The
          balance is due on delivery of the edited files. Invoices are payable within
          7 days unless agreed otherwise in writing.
        </P>
        <Ul items={[
          "Deposits are non-refundable except where we cancel the booking ourselves.",
          "All prices are quoted in Australian Dollars (AUD), exclusive of GST unless stated. GST will be added to the final invoice at the prevailing rate (currently 10%).",
          "Late payments may attract interest at 2% per month, calculated from the invoice due date.",
        ]} />
      </>
    ),
  },
  {
    id: "cancellations",
    title: "Cancellations & rescheduling",
    body: (
      <>
        <P>
          Things happen — kitchens close for repairs, owners get sick, deliveries don&apos;t
          arrive. We&apos;ll always try to work with you. The general rules are:
        </P>
        <Ul items={[
          <><Strong>7+ days notice</Strong> — full reschedule, no fee. Deposit carries over.</>,
          <><Strong>3–7 days notice</Strong> — reschedule with a 50% rebooking fee. Deposit carries over.</>,
          <><Strong>Under 3 days notice</Strong> — deposit forfeit; new deposit required to rebook.</>,
          <><Strong>Day-of cancellation</Strong> — deposit forfeit and you may be liable for any committed travel or assistant costs.</>,
        ]} />
        <P>
          If <Strong>we</Strong> cancel a confirmed booking for any reason other than
          force majeure, your deposit is refunded in full within 5 business days.
        </P>
      </>
    ),
  },
  {
    id: "shoot-day",
    title: "The shoot day",
    body: (
      <>
        <P>
          We arrive on the agreed date with full lighting, surfaces and prop kit. You
          are responsible for:
        </P>
        <Ul items={[
          "Plating the dishes per the agreed brief, in working kitchen condition.",
          "Granting reasonable access to the venue (kitchen, dining room, exterior).",
          "Ensuring relevant staff (chef, barista, owner, marketing lead) are briefed on the visit.",
        ]} />
        <P>
          If access is denied or significant changes to the brief are made on the day,
          we may need to rescope the quote or reschedule.
        </P>
        <P>
          We respect your service. We don&apos;t hold pass plates, occupy critical
          kitchen space during peak prep, or photograph customers without their consent.
        </P>
      </>
    ),
  },
  {
    id: "delivery",
    title: "Delivery",
    body: (
      <>
        <P>
          Standard delivery is <Strong>5–7 business days</Strong> from the shoot date.
          Editorial campaigns and brand-day shoots typically deliver within 7–10
          business days. Files are delivered via a private, downloadable folder link
          (e.g. Dropbox, Google Drive or WeTransfer).
        </P>
        <P>
          Urgent turnarounds (e.g. 48–72 hours) are available where flagged at the time
          of quoting and may incur a rush fee.
        </P>
        <P>
          Once delivered, files remain available for download for at least <Strong>90
          days</Strong>. We back up master files for at least 12 months after delivery
          but cannot guarantee perpetual archival — please keep your own copy.
        </P>
      </>
    ),
  },
  {
    id: "licence",
    title: "Image licence & usage rights",
    body: (
      <>
        <P>
          On full payment, you receive a <Strong>perpetual, worldwide, non-exclusive
          licence</Strong> to use the delivered photography across all of the Client&apos;s
          owned and earned channels, including:
        </P>
        <Ul items={[
          "Your website, paid digital ads (Meta, Google, TikTok) and social media.",
          "In-venue displays, printed menus, posters and signage.",
          "Public relations, press, editorial features and Google Business Profile.",
          "Email marketing, EDM banners and packaging.",
          "Out-of-home (OOH) advertising — transit, billboards, outdoor — at any size.",
        ]} />
        <P>
          The licence covers <Strong>multi-venue groups and franchise networks</Strong>
          under common ownership, as scoped at the time of quoting. Sub-licensing or
          reselling of images to unrelated third parties is not permitted without our
          written consent.
        </P>
      </>
    ),
  },
  {
    id: "copyright",
    title: "Copyright",
    body: (
      <>
        <P>
          Restaurant Photographer retains copyright in all photography produced under
          these terms. The licence granted above is broad and unrestricted across your
          owned and earned channels — but copyright itself stays with the photographer.
        </P>
        <P>
          We may reproduce, share or use any image from a delivered shoot for our own
          portfolio, marketing and social media, unless you request otherwise in
          writing before the shoot date.
        </P>
      </>
    ),
  },
  {
    id: "re-edits",
    title: "Re-edits & revisions",
    body: (
      <>
        <P>
          Every shoot includes <Strong>one free re-edit per delivered image</Strong>
          — colour, crop, exposure or selective retouching adjustments — at no extra
          charge, requested within 30 days of delivery.
        </P>
        <P>
          Reshoots of individual dishes (e.g. plating concerns, kitchen errors) are
          considered on a case-by-case basis. Where the reshoot is required because of
          something we missed, we cover it at no extra cost. Where it&apos;s because
          the dish was different on the day to the brief, a small reshoot fee may
          apply.
        </P>
      </>
    ),
  },
  {
    id: "travel",
    title: "Travel & expenses",
    body: (
      <>
        <P>
          We cover all of <Strong>Greater Melbourne</Strong> with <Strong>no travel
          fees</Strong> inside the metropolitan area. Regional Victoria, interstate and
          multi-day rollouts are quoted with itemised travel and accommodation, agreed
          in writing before the shoot.
        </P>
      </>
    ),
  },
  {
    id: "liability",
    title: "Liability",
    body: (
      <>
        <P>
          Our liability to you for any loss, damage, expense or claim arising out of
          this agreement is limited to <Strong>the fees paid by you for the affected
          shoot</Strong>. We are not liable for indirect, incidental or consequential
          loss (including lost profits, lost bookings or campaign delays) regardless of
          the cause.
        </P>
        <P>
          Nothing in these terms limits any non-excludable consumer guarantees under
          the <em>Australian Consumer Law</em>.
        </P>
      </>
    ),
  },
  {
    id: "force-majeure",
    title: "Force majeure",
    body: (
      <P>
        Neither party is liable for any failure or delay caused by events beyond their
        reasonable control — natural disasters, severe weather, pandemics, public-health
        orders, strikes, lockouts, civil unrest or critical equipment failure. Where a
        shoot can&apos;t proceed because of such an event, we&apos;ll reschedule at the
        earliest mutually-available date without penalty.
      </P>
    ),
  },
  {
    id: "law",
    title: "Governing law",
    body: (
      <P>
        These terms are governed by the laws of the State of Victoria, Australia. Any
        dispute will be resolved in the courts of Victoria. We&apos;ll always try to
        resolve issues by direct conversation first.
      </P>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <>
        <P>For any question about these terms — before, during or after a shoot:</P>
        <Ul items={[
          <>Email: <a href="mailto:hello@restaurantphotographer.com.au" className="text-ember underline-offset-4 hover:underline">hello@restaurantphotographer.com.au</a></>,
          <>Phone: <a href="tel:0494756111" className="text-ember underline-offset-4 hover:underline">0494 756 111</a></>,
          "Studio: Mobile — servicing Greater Melbourne, Victoria",
        ]} />
      </>
    ),
  },
];

export default function Terms() {
  return (
    <>
    <Seo />
    <LegalPageLayout
      breadcrumb="Terms & Conditions"
      eyebrow="Terms & Conditions · Hospitality photography services"
      title={<>The rules,<br /><span className="italic font-light text-ember">in plain English.</span></>}
      intro="How quotes work, what's covered by the licence, when deposits are refunded, and what happens if a shoot needs to move. Written so you can read it in 5 minutes before signing — no lawyer required."
      lastUpdated="15 February 2026"
      sections={SECTIONS}
    />
    </>
  );
}
